import { Stack, Box, Text, Button, useScreenSize } from '@stitch-fix/mode-react';
import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useRouter } from 'next/router';
import LookImage from '../../gallery/LookImage/LookImage';
import FeedbackForm from './FeedbackForm';
import { useSubmitVisionLookFeedbackMutation } from './graphql/submitVisionLookFeedback';
import { useToast } from '../../../../utils/contexts/toast-provider';
import { Look } from '../../../../types/definitions/vendor/graphql-schema.d';
import LookResemblanceAnswer from '../LookResemblanceAnswer/LookResemblanceAnswer';
import styles from './LookFeedback.module.scss';
import ShopTheLook from './ShopTheLook/ShopTheLook';
export type LookFeedbackProps = {
  look: Look;
  shoppableItems: Look['shopProductVariants'];
  isShareUrlEnabled?: boolean;
  onComplete: () => void;
  onDelete?: () => void;
};
const SCROLL_OFFSET = 60;
const scrollToRefWithOffset = (ref: HTMLElement, offset = SCROLL_OFFSET) => {
  const top = ref.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({
    top,
    behavior: 'smooth'
  });
};
const LookFeedback = ({
  look,
  shoppableItems,
  isShareUrlEnabled = false,
  onComplete,
  onDelete
}: LookFeedbackProps) => {
  const {
    addToast
  } = useToast();
  const screenSize = useScreenSize();
  const router = useRouter();
  const [feedbackLook, setFeedbackLook] = useState(look);
  const feedbackFormRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const isLargeDown = screenSize === 'sm' || screenSize === 'md';
  const scrollQuery = router.query?.scroll;
  useEffect(() => {
    if (scrollQuery !== '1' && !hasScrolledRef.current) {
      window.scrollTo(0, 0);
    }
  }, [scrollQuery]);
  useLayoutEffect(() => {
    let lastTop = 0;
    let rafId: number;
    const checkPosition = () => {
      const ref = feedbackFormRef.current;
      if (!ref) return;
      const currentTop = ref.getBoundingClientRect().top + window.scrollY;
      if (currentTop !== lastTop) {
        lastTop = currentTop;
        rafId = requestAnimationFrame(checkPosition);
      } else {
        scrollToRefWithOffset(ref);
        hasScrolledRef.current = true;
        const newQuery = {
          ...router.query
        };
        delete newQuery.scroll;
        router.replace({
          pathname: router.pathname,
          query: newQuery
        }, undefined, {
          shallow: true
        });
      }
    };
    if (isLargeDown && feedbackFormRef.current && scrollQuery === '1') {
      rafId = requestAnimationFrame(checkPosition);
    }
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isLargeDown, scrollQuery, router]);
  const [submitVisionLookFeedback] = useSubmitVisionLookFeedbackMutation({
    onCompleted: data => {
      const result = data.visionLookFeedbackSubmit?.result;
      if (result?.__typename === 'VisionLookFeedbackSubmitSuccess') {
        setFeedbackLook(prev => ({
          ...prev,
          feedbackQuestions: prev.feedbackQuestions.map(q => {
            const updated = result.feedbackQuestions.find(u => u.questionId === q.questionId);
            return updated ? {
              ...q,
              ...updated
            } : q;
          })
        }));
      }
      if (result?.__typename === 'VisionLookFeedbackSubmitError') {
        addToast({
          message: result.message,
          status: 'error'
        });
      }
    },
    onError: apolloError => console.error('GraphQL/Network error:', apolloError.message)
  });
  const handleOnChange = (questionId: string, answer: string) => {
    submitVisionLookFeedback({
      variables: {
        input: {
          lookId: feedbackLook.id,
          answers: [{
            questionId,
            answer
          }]
        }
      }
    });
  };
  const handleResemblanceAnswerChange = (value: string) => {
    setFeedbackLook(prev => ({
      ...prev,
      resemblesMeAnswer: value === 'yes'
    }));
  };
  return <>
      {!isLargeDown && <Box px={1} mb={2}>
          <ShopTheLook items={shoppableItems} />
        </Box>}
      <Box px={{
      sm: 0,
      lg: 1
    }}>
        <Box className={styles.container}>
          <Box className={styles.imageContainer}>
            <LookImage look={feedbackLook} isShareUrlEnabled={isShareUrlEnabled} isZoomEnabled onDelete={onDelete} />
            {isLargeDown && <Box px={1} mt={2}>
                <ShopTheLook items={shoppableItems} />
              </Box>}
          </Box>
          <Box mt={{
          sm: 2,
          lg: 0
        }} px={{
          sm: 1,
          lg: 0
        }}>
            <Box ref={feedbackFormRef} id="feedback">
              <Stack spacing={1}>
                <LookResemblanceAnswer look={feedbackLook} onChange={handleResemblanceAnswerChange} />
                <Stack spacing={1} pt={1}>
                  <FeedbackForm feedbackQuestions={feedbackLook.feedbackQuestions} onChange={handleOnChange} />
                  <Text setting="body-small">
                    We hear you. Your feedback helps Vision improve.
                  </Text>
                  <Button width="fill" onClick={onComplete}>
                    Done
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </>;
};
export default LookFeedback;